//src/components/layout/Layout.jsx
/**
 * Main Layout Component
 * 
 * ✅ UPDATED VERSION - Monitoring Module Integration
 * 
 * Key updates:
 * 1. Added MonitoringRoutes integration for /monitoring/* paths
 * 2. Updated isRoutedModule to include /monitoring
 * 3. Menu click handlers preserve routing for nested modules
 */

import React, { useState } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { 
  Users, 
  Wallet, 
  BadgeDollarSign, 
  Calculator, 
  Settings, 
  ChevronDown, 
  FileText,
  LayoutDashboard,
  UserCog,
  Database,
  DollarSign,
  MonitorCheck,
  PanelTopClose,
  Bell,
  Search,
  Menu,
  X,
  LogOut,
  User
} from 'lucide-react';

// Import Master Data Components
import SPKList from '../../pages/masterdata/spk/SPKList';
import CustomerList from '../../pages/masterdata/customer/CustomerList';
import ProjectList from '../../pages/masterdata/project/ProjectList';
import FOSegmentList from '../../pages/masterdata/fosegment/FOSegmentList';
import TowerSiteList from '../../pages/masterdata/towersite/TowerSiteList';
import ItemList from '../../pages/masterdata/item/ItemList';
import MainAreaList from '../../pages/masterdata/mainarea/MainAreaList';

// Import Project Planning
import ProjectMilestoneList from '../../pages/planning/ProjectMilestoneList';
import AssignFieldSupervisorList from '../../pages/assignment/AssignFieldSupervisorList';
import MilestoneTemplateList from '../../pages/planning/MilestoneTemplateList';

// Import Budgeting Components
import ProjectBOQList from '../../pages/budgeting/boq/ProjectBOQList';
import SegmentBudgetingList from '../../pages/budgeting/fobudgeting/SegmentBudgetingList';
import TowerSiteBudgetingList from '../../pages/budgeting/towerbudgeting/TowerSiteBudgetingList';
import BudgetCategoryList from '../../pages/budgeting/categories/BudgetCategoryList';

// Import Monitoring Components - THESE ARE NOW HANDLED BY ROUTING
// FieldSubmissionList will be accessed via /monitoring/submissions route
// No need to import here anymore for routed modules
import ProgressList5 from '../../pages/monitoring/ProgressList5';
import IssuesList from '../../pages/monitoring/IssuesList';
import RAGStatusList from '../../pages/monitoring/RAGStatusList';
import BudgetRealizationList from '../../pages/monitoring/BudgetRealizationList';

// Import User Management Components
import UserList from '../../pages/usermanagement/UserList';
import RoleList from '../../pages/usermanagement/RoleList';
import PermissionList from '../../pages/usermanagement/PermissionList';

// Import Dashboard
import Dashboard from '../../pages/Dashboard';
import ThemeDashboardExample from '../../pages/ThemeDashboardExample';


const PengaturanContent = () => {
  return <div className="bg-white p-6 rounded-lg shadow">Pengaturan Content</div>;
};

const menuItems = [
  {
    title: 'Dashboard',
    icon: LayoutDashboard,
    subMenus: [
      { 
        title: 'Overview', 
        component: ThemeDashboardExample,
      }
    ]
  },
  {
    title: 'Master Data',
    icon: Database,
    subMenus: [
      { title: 'Customer', component: CustomerList, route: '/masterdata/customers' },
      { title: 'SPK', component: SPKList, route: '/masterdata/spk' },
      { title: 'Project', component: ProjectList, route: '/masterdata/projects' },
      { title: 'FO Segment', component: FOSegmentList, route: '/masterdata/fosegments' },
      { title: 'Tower Site', component: TowerSiteList, route: '/masterdata/towersites' },
      { title: 'Item', component: ItemList, route: '/masterdata/items' },
      { title: 'Main Area & Coefficient', component: MainAreaList, route: '/masterdata/mainareas' }
    ]
  }, 
  {
    title: 'Project Budgeting',
    icon: DollarSign,
    subMenus: [
      { title: 'Bill of Quantity', component: ProjectBOQList },      
      { title: 'FO Segment Budgeting', component: SegmentBudgetingList },
      { title: 'Tower Budgeting', component: TowerSiteBudgetingList },
      { title: 'Budget Categories', component: BudgetCategoryList },
    ]
  },     
  {
    title: 'Project Planning',
    icon: PanelTopClose,
    subMenus: [
      { title: 'Project Milestone', component: ProjectMilestoneList },
      { title: 'Assign Field Supervisor', component: AssignFieldSupervisorList },      
      { title: 'Milestone Template', component: MilestoneTemplateList }
    ]
  },
  {
    title: 'Monitoring',
    icon: MonitorCheck,
    subMenus: [
      // ✅ UPDATED: Field Submissions now uses routing
      { 
        title: 'Field Submissions', 
        route: '/monitoring/submissions'  // Route-based instead of component
      },
      { title: 'Progress', 
        route: 'monitoring/progress'
      },
      //{ title: 'Issues', component: IssuesList },
      { title: 'Issues', 
        route: 'monitoring/issues'
      },
      { title: 'RAG Status', component: RAGStatusList },      
      { title: 'Budget Realization', component: BudgetRealizationList }
    ]
  },
  {
    title: 'User Management',
    icon: User,
    subMenus: [
      { 
        title: 'user', 
        component: UserList,
        route: '/usermanagement'
      },
      { 
        title: 'Role', 
        component: RoleList 
      },
      { 
        title: 'Permission', 
        component: PermissionList 
      }
    ]
  },  
  {
    title: 'Pengaturan',
    icon: Settings,
    subMenus: [
      { title: 'Sub Menu 1', component: PengaturanContent }
    ]
  }
];

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeMenu, setActiveMenu] = useState(menuItems[0]);
  const [activeSubMenu, setActiveSubMenu] = useState(menuItems[0].subMenus[0]);
  const [expandedMenu, setExpandedMenu] = useState(menuItems[0].title);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const username = sessionStorage.getItem('username') || localStorage.getItem('username') || 'Admin';
  const userEmail = sessionStorage.getItem('userEmail') || localStorage.getItem('userEmail') || 'admin@erabangun.co.id';

  const handleMenuClick = (menu) => {
    if (expandedMenu === menu.title) {
      setExpandedMenu(null);
    } else {
      setExpandedMenu(menu.title);
      setActiveMenu(menu);
      setActiveSubMenu(menu.subMenus[0]);
      
      // ✅ FIXED: Always navigate, even for first submenu
      const firstSubMenu = menu.subMenus[0];
      if (firstSubMenu.route) {
        navigate(firstSubMenu.route);
      } else {
        // Navigate to internal path for non-routed modules
        const internalPath = `/internal/${menu.title.toLowerCase().replace(/\s+/g, '-')}/${firstSubMenu.title.toLowerCase().replace(/\s+/g, '-')}`;
        navigate(internalPath);
      }
    }
  };

  // ✅ FIXED: Always navigate to unstick routing
  const handleSubMenuClick = (menu, subMenu) => {
    setActiveMenu(menu);
    setActiveSubMenu(subMenu);
    
    if (subMenu.route) {
      // Has route - navigate to nested route
      navigate(subMenu.route);
    } else {
      // No route - navigate to internal path to force URL change and unstick Outlet
      const internalPath = `/internal/${menu.title.toLowerCase().replace(/\s+/g, '-')}/${subMenu.title.toLowerCase().replace(/\s+/g, '-')}`;
      navigate(internalPath);
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('username');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('user');
    localStorage.removeItem('rememberMe');
    
    toast.success('Logout berhasil', {
      icon: '👋',
      duration: 2000,
    });
    
    navigate('/login', { replace: true });
  };

  // ✅ UPDATED: Added /monitoring to routed modules list
  const isRoutedModule = (path) => {
    const routedPaths = [
      '/usermanagement',
      '/customers',
      '/monitoring',        // ✅ NEW: Monitoring module uses routing
      '/masterdata',
      // Add other routed modules here as you create them:
      // '/projects',
      // '/spk',
      // '/budget',
      // '/planning',
    ];
    
    // Must start with routed path AND not be internal path
    return routedPaths.some(routedPath => 
      path.startsWith(routedPath) && !path.startsWith('/internal')
    );
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <div 
        className={`${
          isSidebarOpen ? 'w-64' : 'w-0'
        } bg-gradient-to-b from-blue-900 to-blue-800 shadow-2xl flex-shrink-0 transition-all duration-300 overflow-hidden`}
      >
        {/* Logo Section */}
        <div className="p-4 border-b border-blue-700 bg-blue-900">
          <div className="flex items-center justify-center">
            <div className="flex items-center gap-3">
              <img 
                src="/LogoEbtel3.png" 
                alt="EraBangun Logo" 
                className="h-12 w-auto object-contain"
              />
              <div className="text-white">
                <div className="font-bold text-lg leading-tight">EraBangun</div>
                <div className="text-xs text-blue-200">PMO Module</div>
              </div>
            </div>
          </div>
        </div>

        {/* Menu Items */}
        <div className="overflow-y-auto h-[calc(100vh-80px)] scrollbar-thin scrollbar-thumb-blue-700 scrollbar-track-blue-900">
          {menuItems.map((menu) => (
            <div key={menu.title}>
              <button
                className={`w-full flex items-center justify-between p-4 transition-all duration-200 ${
                  activeMenu.title === menu.title 
                    ? 'bg-blue-800 text-white border-l-4 border-red-600' 
                    : 'text-blue-100 hover:bg-blue-800 hover:border-l-4 hover:border-blue-600'
                }`}
                onClick={() => handleMenuClick(menu)}
              >
                <div className="flex items-center">
                  <menu.icon className="w-5 h-5 mr-3" />
                  <span className="text-sm font-medium">{menu.title}</span>
                </div>
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${
                    expandedMenu === menu.title ? 'transform rotate-180' : ''
                  }`}
                />
              </button>
              
              {/* Submenu */}
              {expandedMenu === menu.title && (
                <div className="bg-blue-950 bg-opacity-50">
                  {menu.subMenus.map((subMenu) => (
                    <button
                      key={subMenu.title}
                      className={`w-full text-left pl-12 pr-4 py-3 text-sm transition-all duration-200 ${
                        activeSubMenu.title === subMenu.title
                          ? 'text-white bg-red-600 border-l-4 border-red-400 font-medium'
                          : 'text-blue-200 hover:bg-blue-900 hover:text-white hover:pl-14'
                      }`}
                      onClick={() => handleSubMenuClick(menu, subMenu)}
                    >
                      {subMenu.title}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <div className="h-16 bg-blue-200 shadow-md flex items-center justify-between px-6 flex-shrink-0 border-b border-blue-200">
          <div className="flex items-center gap-4">
            {/* Toggle Sidebar Button */}
            <button
              onClick={toggleSidebar}
              className="p-2 rounded-lg hover:bg-blue-200 transition-colors"
            >
              {isSidebarOpen ? (
                <X className="w-5 h-5 text-gray-600" />
              ) : (
                <Menu className="w-5 h-5 text-gray-600" />
              )}
            </button>
            
            {/* Breadcrumb */}
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-gray-800">
                {activeMenu.title}
              </h1>
              <span className="text-gray-400">/</span>
              <span className="text-sm text-red-600 font-medium">
                {activeSubMenu.title}
              </span>
            </div>
          </div>

          {/* Right Section */}
          <div className="flex items-center gap-4">
            {/* Search Bar */}
            <div className="relative hidden md:block">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm w-64 bg-white"
              />
            </div>

            {/* Notification Bell */}
            <button className="p-2 rounded-lg hover:bg-blue-200 transition-colors relative">
              <Bell className="w-5 h-5 text-gray-600" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-600 rounded-full"></span>
            </button>

            {/* User Info with Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-3 pl-4 border-l border-blue-300 hover:bg-blue-200 rounded-lg p-2 transition-colors"
              >
                <div className="text-right hidden md:block">
                  <div className="text-sm font-semibold text-gray-800 capitalize">{username}</div>
                  <div className="text-xs text-gray-500">{userEmail}</div>
                </div>
                <div className="w-10 h-10 bg-gradient-to-br from-blue-900 to-red-600 rounded-full flex items-center justify-center text-white font-bold cursor-pointer">
                  {username.charAt(0).toUpperCase()}
                </div>
              </button>

              {/* User Dropdown Menu */}
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                  <div className="px-4 py-2 border-b border-gray-200">
                    <p className="text-sm font-semibold text-gray-900 capitalize">{username}</p>
                    <p className="text-xs text-gray-500">{userEmail}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="h-0.5 bg-gradient-to-r from-blue-600 via-red-600 to-blue-600 rounded-full"></div>

        {/* Content Area */}
        <div className="flex-1 overflow-auto bg-gray-50">
          <div className="h-full p-6">
            <div className="max-w-full">
              {/* ✅ ROUTING DECISION LOGIC */}
              {isRoutedModule(location.pathname) ? (
                // Use <Outlet /> for routed modules (usermanagement, customers, monitoring)
                <Outlet />
              ) : (
                // Use direct component rendering for non-routed modules
                activeSubMenu.component && <activeSubMenu.component />
              )}
            </div>
          </div>
        </div>

        <div className="h-px bg-gradient-to-r from-blue-600 via-red-600 to-blue-600 rounded-full"></div>

        {/* Footer */}
        <div className="h-12 bg-blue-100 border-t border-blue-200 flex items-center justify-between px-6 flex-shrink-0">
          <div className="text-xs text-gray-600">
            © 2025 PT Era Bangun Jaya. All rights reserved.
          </div>
          <div className="flex items-center gap-4 text-xs text-gray-600">
            <button className="hover:text-blue-900 transition-colors">Privacy Policy</button>
            <span>•</span>
            <button className="hover:text-blue-900 transition-colors">Terms of Service</button>
            <span>•</span>
            <button className="hover:text-blue-900 transition-colors">Help Center</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Layout;